import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

import { surveyHelperContext } from '@/patient-app-common/helpers/survey_helper';
import useSurveyDrafts from '@/hooks/useSurveyDrafts';
import useSegment from '@/hooks/useSegment';
import { useAppSelector } from '@/redux/hooks';

import { SurveyLayout } from './layout/SurveyLayout';
import { SurveyLoader } from './layout/SurveyLoader';
import { QuestionType } from './questions/QuestionType';
import { Review } from './review/Review';

const OPEN_EVENT = 'Survey Opened';
const CLOSE_EVENT = 'Survey Closed';

export function Survey({
  draft,
  eventId,
  idempotencyKey,
  patientDatesInMS,
  patient,
  startTimestamp,
  surveyData,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation('survey');
  const { sanitizeProfile, trackSurvey } = useSegment();
  const { saveDraft } = useSurveyDrafts();
  const { directMode } = useAppSelector(({ auth }) => auth);

  const debouncedSaveDraft = useRef(null);

  const [answerModal, toggleAnswerModal] = useState(false);

  const { profile, medicineHistory } = patient;

  const { questions } = surveyData;
  const surveyName = surveyData.mainTracker
    ? t('common.text.main_tracker')
    : surveyData.patientDisplayName;

  const [surveyHelper, setSurveyHelper] = useState(null);
  const [answers, setAnswers] = useState({});

  const [profilesHistory, setProfileHistory] = useState([profile]);
  const [questionHistory, setQuestionHistory] = useState([0]);

  const index = useMemo(() => {
    return _.last(questionHistory);
  }, [questionHistory]);

  const progress = useMemo(() => {
    if (!index) return 0;

    return _.round(((index + 1) / questions.length) * 100);
  }, [index]);

  const [loader, setLoader] = useState(false);
  const [review, showReview] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [draftDate, setDraftDate] = useState(null);

  useEffect(() => {
    startTimestamp.current = _.now();
  }, []);

  useEffect(() => {
    trackSurvey(
      OPEN_EVENT,
      {
        id: eventId,
        profile: sanitizeProfile(profile),
      },
      surveyData
    );
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [questionHistory]);

  useEffect(() => {
    const helper = surveyHelperContext({
      patientDatesInMS,
      surveyData,
      editMode,
    });
    setSurveyHelper(helper);
  }, [editMode, patientDatesInMS, surveyData]);

  useEffect(() => {
    if (!draft) return;
    if (draftDate > startTimestamp.current) return;

    const { draftHistory, draftAnswers, draftProfiles, draftCreatedAt } = draft;

    setQuestionHistory(draftHistory);
    setAnswers(draftAnswers);
    setProfileHistory(draftProfiles);
    setDraftDate(draftCreatedAt);

    if (_.last(draftHistory) === 0 && draftHistory.length > 1) {
      showReview(true);
    }
  }, [draft, draftDate]);

  useEffect(() => {
    debouncedSaveDraft.current = _.debounce((data) => {
      saveDraft(data);
      setDraftDate(_.now());
    }, 5000);

    return () => {
      debouncedSaveDraft.current.cancel();
    };
  }, []);

  const handleSave = useCallback(() => {
    if (_.isEmpty(answers) || _.isEqual(draft?.draftAnswers, answers)) return;

    debouncedSaveDraft.current({
      answers,
      eventId,
      surveyData,
      index,
    });
  }, [answers, eventId, draft, surveyData]);

  const getAnswer = (value) => {
    const answersClone = { ...answers };
    const questionID = questions[index].id;
    answersClone[questionID] = value;
    setAnswers(answersClone);
  };

  const handleNext = () => {
    const questionType = questions[index].type;
    const answer = answers[questions[index].id];

    const valid = surveyHelper.validateAnswer(questionType, answer);

    if (!valid) {
      toggleAnswerModal(true);
      return;
    }

    handleSave();

    const { validQuestions, validProfiles, validAnswers } =
      surveyHelper.validateQuestions({
        currentIndex: index,
        currentAnswers: answers,
        currentHistory: questionHistory,
        currentProfiles: profilesHistory,
      });

    const nextIndex = _.last(validQuestions);

    if (nextIndex === 0) {
      showReview(true);
    }

    setProfileHistory([...validProfiles]);
    setQuestionHistory([...validQuestions]);
    setAnswers(validAnswers);
  };

  const handleBack = () => {
    const initProfilesHistory = _.initial([...profilesHistory]);
    setProfileHistory(initProfilesHistory);

    const initQuestionHistory = _.initial([...questionHistory]);
    setQuestionHistory(initQuestionHistory);
  };

  const handleEdit = (editIndex) => {
    showReview(false);
    setEditMode(true);

    const pos = questionHistory.indexOf(editIndex);

    const shallowProfiles = profilesHistory.slice(0, pos + 1);
    setProfileHistory(shallowProfiles);

    const shallowQuestions = questionHistory.slice(0, pos + 1);
    setQuestionHistory(shallowQuestions);
  };

  const handleSaveAndExit = async () => {
    trackSurvey(CLOSE_EVENT, { id: eventId, progress }, surveyData);

    if (_.isEmpty(answers)) {
      return navigate('/');
    }

    setLoader({ text: t('survey.saving_draft') });

    debouncedSaveDraft.current?.cancel();

    try {
      await saveDraft({
        answers,
        eventId,
        surveyData,
        index,
      });
      navigate('/');
    } catch (e) {
      console.error(e);
      setLoader({
        text: t('common.error.server'),
        type: 'error',
      });
    }
  };

  if (loader) {
    return (
      <SurveyLoader
        text={loader.text}
        type={loader.type}
        handleBack={() => setLoader(null)}
      />
    );
  }

  if (review) {
    return (
      <>
        <Review
          answers={answers}
          eventId={eventId}
          handleEdit={handleEdit}
          idempotencyKey={idempotencyKey}
          medicineHistory={medicineHistory}
          profile={_.last(profilesHistory)}
          questions={questions}
          surveyData={surveyData}
        />
      </>
    );
  }

  return (
    <SurveyLayout
      answerModal={answerModal}
      draftDate={draftDate}
      disableClose={surveyData.setupSurvey || directMode}
      handleSaveAndExit={handleSaveAndExit}
      handleNext={handleNext}
      handleBack={handleBack}
      index={index}
      options={questions[index].options}
      progress={progress}
      subtitle={questions[index].subtitle}
      showBack={!editMode}
      surveyName={surveyName}
      title={questions[index].title}
      toggleAnswerModal={toggleAnswerModal}
    >
      <QuestionType
        answers={answers}
        getAnswer={getAnswer}
        index={index}
        medicineHistory={medicineHistory}
        questions={questions}
      />
    </SurveyLayout>
  );
}
